import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Lightweight tooltip that renders into document.body via a portal, so it is
 * never clipped by a parent's overflow or border-radius constraints.
 *
 * Usage:
 *   <Tooltip label="Copy share link">
 *     <button>...</button>
 *   </Tooltip>
 */
const Tooltip = ({
  label,
  children,
  position = 'top',
  eyebrow = null,
  wrapperClassName = 'relative inline-flex',
  wrapperStyle = undefined,
}) => {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const tooltipIdRef = useRef(`tooltip-${Math.random().toString(36).slice(2, 10)}`);
  const dividerIndex = typeof label === 'string' ? label.indexOf(':') : -1;
  const title = dividerIndex > -1 ? label.slice(0, dividerIndex).trim() : '';
  const body = dividerIndex > -1 ? label.slice(dividerIndex + 1).trim() : label;

  useEffect(() => {
    if (!open) return undefined;

    const handleWindowChange = () => updateCoords();
    window.addEventListener('scroll', handleWindowChange, true);
    window.addEventListener('resize', handleWindowChange);
    return () => {
      window.removeEventListener('scroll', handleWindowChange, true);
      window.removeEventListener('resize', handleWindowChange);
    };
  }, [open]);

  const updateCoords = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    if (position === 'top') {
      setCoords({
        top: rect.top - 6,
        left: rect.left + rect.width / 2,
      });
    } else {
      setCoords({
        top: rect.bottom + 6,
        left: rect.left + rect.width / 2,
      });
    }
  };

  const handleMouseEnter = () => {
    updateCoords();
    setOpen(true);
  };

  const handleMouseLeave = () => setOpen(false);

  return (
    <span
      ref={triggerRef}
      className={wrapperClassName}
      style={wrapperStyle}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      aria-describedby={open && label ? tooltipIdRef.current : undefined}
    >
      {children}
      {open && label && ReactDOM.createPortal(
        <span
          className="fixed z-[9999] pointer-events-none"
          style={{
            top: coords.top,
            left: coords.left,
            transform: position === 'top'
              ? 'translate(-50%, -100%)'
              : 'translate(-50%, 0)',
          }}
        >
          <span
            id={tooltipIdRef.current}
            role="tooltip"
            className="relative block max-w-[18rem] rounded-card border border-border-card bg-surface px-3 py-2 shadow-card"
          >
            <span
              className={`pointer-events-none absolute left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-border-card bg-surface ${
                position === 'top'
                  ? 'top-full -translate-y-1/2 border-b border-r'
                  : 'bottom-full translate-y-1/2 border-l border-t'
              }`}
            />
            {eyebrow ? (
              <span className="mb-1 block text-[0.62rem] font-sans font-extrabold uppercase tracking-[0.16em] text-mustard">
                {eyebrow}
              </span>
            ) : null}
            {title ? (
              <>
                <span className="block font-display text-[0.92rem] font-bold leading-tight text-ds-text">
                  {title}
                </span>
                <span className="mt-1 block whitespace-normal text-xs font-sans font-semibold leading-relaxed text-text-muted">
                  {body}
                </span>
              </>
            ) : (
              <span className="block whitespace-normal text-xs font-sans font-semibold leading-relaxed text-ds-text">
                {body}
              </span>
            )}
          </span>
        </span>,
        document.body
      )}
    </span>
  );
};

export default Tooltip;
